import { Marked } from "marked";
// @ts-expect-error — no type declarations for marked-terminal
import { markedTerminal } from "marked-terminal";

const marked = new Marked(markedTerminal());

/**
 * Accumulates streamed markdown text and renders it to the terminal
 * with ANSI formatting (bold, italic, code blocks, etc.).
 *
 * Strategy: accumulate full text, re-render on each write, output only
 * the newly rendered portion. Completed blocks (separated by double
 * newlines) are "committed" so re-rendering only processes the tail.
 * @docLink packages/runner/dev-guide#agent-chat-single-shot
 */
export class MarkdownStreamer {
  private buffer = "";
  private committedSource = "";
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: write-only accumulator kept for future diff-based rendering
  private committedRender = "";
  private output: NodeJS.WriteStream;

  constructor(output: NodeJS.WriteStream = process.stdout) {
    this.output = output;
  }

  write(chunk: string): void {
    this.buffer += chunk;
    this.flush();
  }

  /** Render any remaining content and reset state. */
  end(): void {
    if (this.buffer.length > this.committedSource.length) {
      const tail = this.buffer.slice(this.committedSource.length);
      if (tail.trim()) {
        const rendered = this.render(tail);
        this.output.write(rendered);
      }
    }
    this.buffer = "";
    this.committedRender = "";
    this.committedSource = "";
  }

  private flush(): void {
    // Find the last double-newline boundary — everything before it is
    // structurally complete markdown that won't change with more input.
    const uncommitted = this.buffer.slice(this.committedSource.length);
    const boundary = uncommitted.lastIndexOf("\n\n");
    if (boundary === -1) return;

    const complete = uncommitted.slice(0, boundary + 2);
    const rendered = this.render(complete);
    this.output.write(rendered);

    this.committedSource += complete;
    this.committedRender += rendered;
  }

  private render(src: string): string {
    try {
      const result = marked.parse(src);
      if (typeof result === "string") return result;
      // marked v15 can return a Promise if async extensions are used,
      // but with marked-terminal it is always synchronous.
      return String(result);
    } catch {
      // Fallback: output raw text if rendering fails
      return src;
    }
  }
}
